// Environment configuration loader
export interface EnvironmentConfig {
  port: string;
  oktaIssuer: string;
  oktaClientId: string;
  oktaRedirectUri: string;
  oktaPostLogoutRedirectUri: string;
  environment: string;
  baseUrl: string;
  apiBaseUrl: string;
  // Session and token management settings
  sessionTimeoutMinutes: number;
  tokenRefreshBufferMinutes: number;
  autoTokenRefreshEnabled: boolean;
}

// Get current environment from NODE_ENV or REACT_APP_ENVIRONMENT
export const getCurrentEnvironment = (): string => {
  const env = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'local';
  
  // Map NODE_ENV to our environment names
  if (env === 'local') return 'local';
  if (env === 'development') return 'dev';
  if (env === 'production') return 'prod';
  if (env === 'staging') return 'beta';
  
  return env; // Return as-is for local, dev, beta, prod
};

// Load environment-specific configuration
export const loadEnvironmentConfig = (): EnvironmentConfig => {
  const currentEnv = getCurrentEnvironment();
  
  // Default configuration that works for all environments
  const config: EnvironmentConfig = {
    port: process.env.PORT || '8333',
    oktaIssuer: process.env.REACT_APP_OKTA_ISSUER || '',
    oktaClientId: process.env.REACT_APP_OKTA_CLIENT_ID || '',
    oktaRedirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI || '',
    oktaPostLogoutRedirectUri: process.env.REACT_APP_OKTA_POST_LOGOUT_REDIRECT_URI || '',
    environment: process.env.REACT_APP_ENVIRONMENT || currentEnv,
    baseUrl: process.env.REACT_APP_BASE_URL || '',
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || '',
    // Session and token management settings with defaults
    sessionTimeoutMinutes: parseInt(process.env.REACT_APP_SESSION_TIMEOUT_MINUTES || '60', 10), // 1 hour default
    tokenRefreshBufferMinutes: parseInt(process.env.REACT_APP_TOKEN_REFRESH_BUFFER_MINUTES || '5', 10), // 5 minutes before expiration
    autoTokenRefreshEnabled: process.env.REACT_APP_AUTO_TOKEN_REFRESH_ENABLED !== 'false', // Enabled by default
  };

  // Validate required configuration
  const requiredFields = ['oktaIssuer', 'oktaClientId', 'oktaRedirectUri', 'oktaPostLogoutRedirectUri'];
  const missingFields = requiredFields.filter(field => !config[field as keyof EnvironmentConfig]);
  
  if (missingFields.length > 0) {
    console.error(`Missing required environment variables: ${missingFields.join(', ')}`);
    console.error(`Current environment: ${currentEnv}`);
    console.error('Please ensure the correct .env file is loaded for your environment.');
  }

  return config;
};

// Export the loaded configuration
export const envConfig = loadEnvironmentConfig();

// Helper functions for specific configuration values
export const getApiBaseUrl = (): string => {
  return envConfig.apiBaseUrl;
};

export const getBaseUrl = (): string => {
  return envConfig.baseUrl || window.location.origin;
};

export const isProduction = (): boolean => {
  return envConfig.environment === 'prod';
};

export const isDevelopment = (): boolean => {
  return envConfig.environment === 'dev';
};

export const isLocal = (): boolean => {
  return envConfig.environment === 'local';
};

export const isBeta = (): boolean => {
  return envConfig.environment === 'beta';
};